/**
 * Structured AI Chat Interface
 * 
 * Interactive chat for email and calendar management using GPT-5 nano structured agents.
 * Provides real-time feedback on AI thinking, confidence, and structured actions.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { structuredEmailAI } from '@/services/structuredEmailAI';
import { Send, Loader, CheckCircle, AlertCircle, Brain } from 'lucide-react';
import { AgentModeToggle } from '@/components/AgentModeToggle';
import { orchestratorMiddleware } from '@/services/orchestratorMiddleware';
import type { AgentMode } from '@/types/agentMode';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  agentResponse?: any;
  timestamp: Date;
  isLoading?: boolean;
}

interface StructuredAIChatProps {
  userId?: string;
}

const formatSuccessMessage = (result: any): string => {
  let message = '';

  // Confidence indicator
  const confidence = result.confidence || 0;
  const confidenceBar = getConfidenceIndicator(confidence);
  message += `${confidenceBar} Confidence: ${confidence}%\n`;

  // Action details
  message += `\n✅ Action: ${result.action}\n`;
  message += `${result.reasoning}\n`;

  // Results
  if (result.result) {
    message += `\n📊 Results:\n`;
    if (result.result.action === 'email_send') {
      message += `• Email sent to: ${result.result.recipients.join(', ')}\n`;
      message += `• Message ID: ${result.result.messageId}\n`;
    } else if (result.result.action === 'email_search') {
      message += `• Found ${result.result.resultsCount} emails\n`;
      message += `• Query: ${result.result.query}\n`;
    } else if (result.result.action === 'calendar_create') {
      message += `• Event: ${result.result.title}\n`;
      message += `• Start: ${result.result.start}\n`;
    }
  }

  // Alternatives
  if (result.alternatives && result.alternatives.length > 0) {
    message += `\n💡 Alternative approaches:\n`;
    result.alternatives.forEach((alt: any) => {
      message += `• ${alt.type}: ${alt.reason}\n`;
    });
  }

  // Warnings
  if (result.warnings && result.warnings.length > 0) {
    message += `\n⚠️ Warnings:\n`;
    result.warnings.forEach((warning: string) => {
      message += `• ${warning}\n`;
    });
  }

  return message;
};

const getConfidenceIndicator = (confidence: number): string => {
  if (confidence >= 90) return '🟢';
  if (confidence >= 70) return '🟡';
  return '🔴';
};

export const StructuredAIChat: React.FC<StructuredAIChatProps> = ({ userId: propUserId }) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Hello! I\'m your AI email assistant powered by GPT-5 nano structured agents. I can help you with:\n\n📧 Email Management: Send, search, reply, move emails\n📅 Calendar: Create events, find meetings\n🤖 Smart Actions: Analyze emails, generate drafts, create automation rules\n\nWhat would you like to do?',
      timestamp: new Date()
    }
  ]);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(propUserId || null);
  const [agentMode, setAgentMode] = useState<AgentMode>('action');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Get authenticated user from Blink and initialize AI services
  useEffect(() => {
    const initUser = async () => {
      try {
        const { blink } = await import('@/lib/blink');
        const user = await blink.auth.me();
        setCurrentUserId(user.id);
        structuredEmailAI.setUserId(user.id);
        
        // Initialize orchestrator with user context
        await orchestratorMiddleware.initialize(user.id);
        
        // Get current agent mode from orchestrator
        const currentMode = orchestratorMiddleware.getAgentMode();
        setAgentMode(currentMode);
        
        console.log('✅ AI Chat initialized with user:', user.id, 'Mode:', currentMode);
      } catch (error) {
        console.error('Failed to initialize user:', error);
      }
    };

    if (!propUserId) {
      initUser();
    } else {
      setCurrentUserId(propUserId);
      structuredEmailAI.setUserId(propUserId);
    }
  }, [propUserId]);

  // Handler for mode changes
  const handleModeChange = async (mode: AgentMode) => {
    setAgentMode(mode);
    await orchestratorMiddleware.setAgentMode(mode);
    toast.success(`Switched to ${mode === 'answer' ? 'Answer' : 'Action'} Mode`);
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    // Verify user is authenticated
    if (!currentUserId) {
      toast.error('Please sign in to use AI features');
      return;
    }

    // Add user message
    const userMessage: Message = {
      id: `msg_${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    // Add loading message
    const loadingMessage: Message = {
      id: `loading_${Date.now()}`,
      role: 'assistant',
      content: 'Processing your request with live Graph API...',
      timestamp: new Date(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      console.log('🔄 Executing AI action for user:', currentUserId);
      
      // Execute user intent with structured AI (uses real Graph API via stored auth tokens)
      const result = await structuredEmailAI.executeUserIntent(input);

      // Remove loading message
      setMessages(prev => prev.filter(m => !m.isLoading));

      if (result.success) {
        const assistantMessage: Message = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: formatSuccessMessage(result),
          agentResponse: result,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
        toast.success(`Action completed: ${result.action}`);
      } else {
        const errorMessage: Message = {
          id: `msg_${Date.now()}`,
          role: 'assistant',
          content: `❌ Action failed: ${result.error}${result.warnings ? `\n⚠️ Warnings: ${result.warnings.join(', ')}` : ''}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorMessage]);
        toast.error(result.error);
      }
    } catch (error) {
      // Remove loading message
      setMessages(prev => prev.filter(m => !m.isLoading));

      const errorMessage: Message = {
        id: `msg_${Date.now()}`,
        role: 'assistant',
        content: `❌ Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      toast.error('Failed to process request');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setInput(example);
  };

  const examples = [
    'Send an email to john@example.com about the project update',
    'Search for emails from last week with attachments',
    'Create a meeting for tomorrow at 2 PM with team members',
    'Analyze the sentiment of my recent email from Sarah',
    'Generate a reply to that important message',
    'Create an automation rule for important emails from clients'
  ];

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-amber-50 to-orange-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-400 to-orange-400 text-amber-950 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5" />
              <h1 className="font-semibold">GPT-5 Nano Structured AI Agent</h1>
            </div>
            <p className="text-sm mt-1 opacity-80">Reliable email & calendar automation with got-5 nano format</p>
          </div>
          <AgentModeToggle
            currentMode={agentMode}
            onModeChange={handleModeChange}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4 max-w-4xl mx-auto">
          {messages.length === 1 && (
            // Show examples when empty
            <div className="mt-8">
              <p className="text-sm font-semibold text-amber-950 mb-3">Try these examples:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {examples.map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleExampleClick(example)}
                    className="p-3 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 text-left text-sm transition-colors"
                  >
                    <span className="text-amber-600 font-semibold">→</span> {example}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              {message.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-amber-950" />
                </div>
              )}

              <div
                className={`max-w-xl p-3 rounded-lg ${
                  message.role === 'user'
                    ? 'bg-gradient-to-br from-amber-500 to-orange-500 text-white'
                    : message.isLoading
                    ? 'bg-white border border-amber-200'
                    : 'bg-white border border-amber-200'
                }`}
              >
                {message.isLoading ? (
                  <div className="flex items-center gap-2">
                    <Loader className="w-4 h-4 animate-spin" />
                    {message.content}
                  </div>
                ) : (
                  <>
                    <p className="whitespace-pre-wrap text-sm font-medium">{message.content}</p>
                    {message.agentResponse && (
                      <div className="mt-3 pt-3 border-t border-amber-200 text-xs text-amber-700 space-y-1">
                        <div className="flex items-center gap-1">
                          {message.agentResponse.result?.success !== false ? (
                            <CheckCircle className="w-3 h-3 text-green-600" />
                          ) : (
                            <AlertCircle className="w-3 h-3 text-red-600" />
                          )}
                          <span>Status: {message.agentResponse.result?.success ? 'Success' : 'Failed'}</span>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      <div className="border-t border-amber-200 bg-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            placeholder="Describe what you'd like to do with your emails or calendar..."
            className="flex-1 border-amber-200 focus:border-amber-400 focus:ring-amber-400"
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4"
          >
            {isLoading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StructuredAIChat;
